/* =========================================================================
   JOMIKUTO · facets.js
   <jomi-facets> wraps a collection's filter form + product list.
   On filter/sort change: fetches the new URL, extracts just the product list
   and facet state, updates the DOM without a full page reload.
   ========================================================================= */

class JomiFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-jomi-facets-form]');
    this.resultsTarget = this.querySelector('[data-jomi-facets-results]');
    this.togglePanel = this.querySelector('[data-jomi-facets-panel]');
    this.toggleBtn = this.querySelector('[data-jomi-facets-toggle]');
    this.clearAllLink = this.querySelector('[data-jomi-facets-clear-all]');

    if (!this.form || !this.resultsTarget) return;

    // Filter toggle (expand/collapse the filter panel)
    this.toggleBtn?.addEventListener('click', () => {
      const isOpen = this.togglePanel.getAttribute('data-open') === 'true';
      this.togglePanel.setAttribute('data-open', !isOpen);
      this.toggleBtn.setAttribute('aria-expanded', !isOpen);
    });

    // Form change → debounced refresh
    let timer;
    this.form.addEventListener('change', (e) => {
      clearTimeout(timer);
      timer = setTimeout(() => this.#refresh(), 250);
    });

    // Remove-filter pill clicks (intercept, use AJAX)
    this.addEventListener('click', (e) => {
      const removeLink = e.target.closest('[data-jomi-facet-remove]');
      if (removeLink) {
        e.preventDefault();
        this.#navigate(removeLink.href);
      }
    });

    // Browser back/forward
    window.addEventListener('popstate', () => this.#refresh(window.location.href, /*pushState=*/false));
  }

  #refresh(urlOverride, pushState = true) {
    const url = urlOverride || this.#formToUrl();
    this.#navigate(url, pushState);
  }

  #formToUrl() {
    const params = new URLSearchParams();
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      if (value !== '' && value != null) params.append(key, value);
    }
    const base = this.dataset.url || window.location.pathname;
    return `${base}?${params.toString()}`;
  }

  async #navigate(url, pushState = true) {
    this.#setLoading(true);
    try {
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Replace the facets wrapper (which contains both filters and results)
      const newWrapper = doc.querySelector('jomi-facets');
      if (!newWrapper) return;

      // Replace the results block
      const newResults = newWrapper.querySelector('[data-jomi-facets-results]');
      if (newResults) this.resultsTarget.innerHTML = newResults.innerHTML;

      // Replace the active pills + filter state (checkboxes may have shifted counts)
      const newPanel = newWrapper.querySelector('[data-jomi-facets-panel]');
      if (newPanel && this.togglePanel) {
        const wasOpen = this.togglePanel.getAttribute('data-open') === 'true';
        this.togglePanel.innerHTML = newPanel.innerHTML;
        this.togglePanel.setAttribute('data-open', wasOpen);
      }

      const newPills = newWrapper.querySelector('[data-jomi-facets-pills]');
      const existingPills = this.querySelector('[data-jomi-facets-pills]');
      if (newPills && existingPills) existingPills.innerHTML = newPills.innerHTML;

      const newCount = newWrapper.querySelector('[data-jomi-facets-count]');
      const existingCount = this.querySelector('[data-jomi-facets-count]');
      if (newCount && existingCount) existingCount.innerHTML = newCount.innerHTML;

      if (pushState) history.pushState({}, '', url);
      // Scroll to top of results for clarity
      this.resultsTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error('[Jomi facets]', err);
      // Fall back to full navigation
      window.location.href = url;
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(isLoading) {
    if (isLoading) {
      this.resultsTarget.setAttribute('aria-busy', 'true');
      this.resultsTarget.classList.add('is-loading');
    } else {
      this.resultsTarget.removeAttribute('aria-busy');
      this.resultsTarget.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('jomi-facets')) {
  customElements.define('jomi-facets', JomiFacets);
}
